import {
  ReactElement,
  Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { MinusSvg, PlusSvg } from '@cashier/cashier-assets';
import { Directions, InputModes, InputTypes } from '@cashier/cashier-model';
import {
  addDecimals,
  AmountConverter,
  compareDecimals,
  eventBus,
  EventFactory,
  isDecimalString,
  roundDecimalToScale,
  subtractDecimals,
} from '@cashier/cashier-shared';
import { cn, maskInput } from '@cashier/cashier-ui';

import styles from './styles.module.scss';

interface InputApi {
  ref?: Ref<InputApi>;
  getValue: () => string;
  setValue: (value: string) => void;
}

interface InputProps {
  ref?: Ref<InputApi>;
  name: string;
  label: string;
  type: InputTypes;
  value: string;
  isError?: boolean;
  isDisabled?: boolean;
  min: number;
  max: number;
  step?: string | null;
  scale?: number;
  onChange: (value: string, isFocused: boolean) => void;
  direction: Directions;
  currency: string | null;
}

const DEFAULT_STEP = '1';

const toComparable = (value: string | number): string | null => {
  const raw = String(value ?? '').replaceAll(',', '');

  return isDecimalString(raw) ? raw : null;
};

export const Input = ({
  ref,
  name,
  label,
  type,
  value,
  isError = false,
  isDisabled = false,
  min,
  max,
  step,
  scale,
  onChange,
  currency,
  direction,
}: InputProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  const [currentValue, setCurrentValue] = useState(value);

  const stepSize = step && isDecimalString(step) ? step : DEFAULT_STEP;
  const minBound = toComparable(min);
  const maxBound = toComparable(max);
  const amount = toComparable(currentValue) ?? '';

  const minAmount = AmountConverter.toDelimitedNumber(
    AmountConverter.exponentialNotationToNumber(String(min)),
  );

  const maxAmount = AmountConverter.toDelimitedNumber(
    AmountConverter.exponentialNotationToNumber(String(max)),
  );

  useEffect(() => {
    if (!inputRef?.current) return;
    if (value && value !== inputRef.current.value) {
      inputRef.current.value = value;
      setCurrentValue(value);
    }
  }, [value]);

  useImperativeHandle(ref, () => ({
    getValue: () => inputRef.current?.value ?? '',
    setValue: (nextValue) => {
      if (inputRef.current) {
        inputRef.current.value = nextValue;
      }

      setCurrentValue(nextValue);
    },
  }));

  const onChangeHandler = (): void => {
    if (!inputRef?.current) return;

    maskInput(inputRef, name, type);
    setCurrentValue(inputRef.current.value);

    onChange(
      inputRef.current.value,
      document.activeElement === inputRef.current,
    );
  };

  const onFocusHandler = (): void => {
    if (!inputRef?.current) return;

    onChange(inputRef.current.value, true);
    eventBus.emit(EventFactory.onFieldFocused(name));
  };

  const onBlurHandler = (): void => {
    if (!inputRef?.current) return;

    onChange(inputRef.current.value, false);
  };

  const applyStep = (direction: 1 | -1): void => {
    if (!inputRef.current || isDisabled) return;

    let nextValue: string;

    if (!amount) {
      if (direction === -1) return;
      nextValue = minBound ?? stepSize;
    } else if (direction === -1) {
      nextValue = subtractDecimals(amount, stepSize);
    } else {
      nextValue = addDecimals(amount, stepSize);
    }

    if (scale !== undefined) {
      nextValue = roundDecimalToScale(nextValue, scale);
    }

    if (minBound && compareDecimals(nextValue, minBound) < 0) {
      const isUnderLimitAlready =
        !!amount && compareDecimals(amount, minBound) <= 0;

      if (direction === 1 || isUnderLimitAlready) return;

      nextValue = minBound;
    }

    if (maxBound && compareDecimals(nextValue, maxBound) > 0) {
      const isOverLimitAlready =
        !!amount && compareDecimals(amount, maxBound) >= 0;

      if (direction === -1 || isOverLimitAlready) return;

      nextValue = maxBound;
    }

    inputRef.current.value = nextValue;
    setCurrentValue(nextValue);
    onChange(nextValue, false);
    eventBus.emit(EventFactory.onAmountChosen(nextValue));
  };

  const isDecreaseDisabled =
    isDisabled ||
    !amount ||
    (!!minBound && compareDecimals(amount, minBound) <= 0);

  const isIncreaseDisabled =
    isDisabled ||
    (!!amount && !!maxBound && compareDecimals(amount, maxBound) >= 0);

  return (
    <div className={styles.container}>
      <div
        className={cn([
          styles.inputWrapper,
          isError && styles.inputWrapperError,
        ])}
        data-test="amount-input-container"
      >
        <button
          className={cn({
            [styles.stepperButton]: true,
            [styles.stepperMinus]: true,
            [styles.rtl]: direction === Directions.Rtl,
          })}
          type="button"
          onClick={() => applyStep(-1)}
          disabled={isDecreaseDisabled}
          aria-label="decrease amount"
          data-test="amount-stepper-minus"
        >
          <span className={styles.stepperIcon}>
            <MinusSvg />
          </span>
        </button>

        <div className={styles.inputValueContainer}>
          <div
            className={cn({
              [styles.valueGroup]: true,
              [styles.rtl]: direction === Directions.Rtl,
            })}
          >
            {currency && (
              <div
                className={styles.currency}
                data-test="amount-input-currency"
              >
                {currency}
              </div>
            )}

            <input
              data-test="amount-input"
              id={name}
              type={type || InputTypes.Text}
              name={name}
              ref={inputRef}
              onChange={onChangeHandler}
              min={0}
              autoComplete="off"
              disabled={isDisabled}
              onFocus={onFocusHandler}
              onBlur={onBlurHandler}
              inputMode={
                type === InputTypes.Integer
                  ? InputModes.Numeric
                  : InputModes.Decimal
              }
              className={styles.input}
              size={1}
            />
          </div>
        </div>

        <button
          className={cn({
            [styles.stepperButton]: true,
            [styles.stepperPlus]: true,
            [styles.rtl]: direction === Directions.Rtl,
          })}
          type="button"
          onClick={() => applyStep(1)}
          disabled={isIncreaseDisabled}
          aria-label="increase amount"
          data-test="amount-stepper-plus"
        >
          <span className={styles.stepperIcon}>
            <PlusSvg />
          </span>
        </button>
      </div>

      <div className={cn([styles.metaContainer])}>
        <label
          className={cn([styles.metaLabel])}
          htmlFor={name}
          data-test="amount-input-label"
        >
          {label}
        </label>

        <div className={cn([styles.limits])} data-test="limits-placeholder">
          {minAmount && `${t('min.label')} ${currency} ${minAmount}`} -{' '}
          {maxAmount && `${t('max.label')} ${currency} ${maxAmount}`}
        </div>
      </div>
    </div>
  );
};
